import { cn } from '@/lib/cn';

type StarSize = 'sm' | 'md' | 'lg';

const ICON_SIZE: Record<StarSize, string> = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-2xl',
};

const VALUE_SIZE: Record<StarSize, string> = {
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-lg',
};

interface StarsRatingProps {
  /** Star value; may be fractional for an average, or an integer per review. */
  value: number;
  size?: StarSize;
  /** Also render the numeric value next to the stars. */
  showValue?: boolean;
  className?: string;
}

/** Read-only star display backed by the brand accent colour (never clickable). */
export default function StarsRating({
  value,
  size = 'md',
  showValue = false,
  className,
}: StarsRatingProps) {
  const filled = Math.round(value);
  const label = Number.isInteger(value) ? String(value) : value.toFixed(1);

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <span className="inline-flex items-center" role="img" aria-label={`${label} / 5`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={cn(
              star <= filled ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300',
              ICON_SIZE[size],
            )}
            aria-hidden="true"
          />
        ))}
      </span>
      {showValue ? (
        <span className={cn('font-semibold text-foreground-900', VALUE_SIZE[size])}>{label}</span>
      ) : null}
    </span>
  );
}